import parsePhoneNumber from 'libphonenumber-js/min';
import { find } from 'lodash-es';
import * as yup from 'yup';
import {
  BOOLEAN_STATUS,
  GENERAL_FIELD_TYPE,
} from '../../../../constants/userSetting';
import { BIRTHDAY_ERROR } from '../../../../shop/constants/user';
import { getJsMonth, validateBirthday } from '../../../../utils/birthday';
import { validateEmail } from '../../../../utils/validators';
import { validatePhone } from '../../../../utils/intlTel';
import { FIELD } from './constants/field';

const PHONE_REGEX = /^\+?[()\-#.\s\d]+$/;
const PHONE_SCHEMA = yup
  .string()
  .trim()
  .min(6, 'form.validation.minlength')
  .max(20, 'form.validation.maxlength')
  .matches(PHONE_REGEX, 'form.validation.invalid');

export default ({
  userData,
  userSetting,
  addressSchemaMap,
  hasCountryCode,
}) => {
  const customFields = (userSetting?.custom_fields || []).reduce(
    (result, field) => {
      if (
        field.field_type !== 'readable' &&
        field.options.profile.required === BOOLEAN_STATUS.TRUE
      ) {
        result[field.field_id] = yup
          .string()
          .trim()
          .required('form.validation.required');
      }
      return result;
    },
    {},
  );

  const genderFieldSetting = userSetting.general_fields?.find(
    ({ type }) => type === GENERAL_FIELD_TYPE.GENDER,
  );
  const genderSchema =
    genderFieldSetting?.options?.profile?.required === BOOLEAN_STATUS.TRUE
      ? { [FIELD.GENDER]: yup.string().required('form.validation.required') }
      : {};

  return yup.object().shape({
    [FIELD.NAME]: yup
      .string()
      .trim()
      .required('form.validation.required')
      .matches(
        /^[\p{L}\p{M}\p{N}\p{So}\p{Sk}\s.\-@/_()]+$/u,
        'form.validation.format',
      )
      .max(50, 'form.validation.maxlength'),
    [FIELD.EMAIL]: yup
      .string()
      .nullable(true)
      .trim()
      .max(50, 'form.validation.maxlength')
      .test('custom', 'form.validation.invalid', (value) => {
        if (!value) {
          return true;
        }
        return validateEmail(value);
      })
      .when([], {
        is: () => !userData[FIELD.MOBILE_PHONE],
        then: yup.string().required('form.validation.required').nullable(true),
      }),
    [FIELD.COUNTRY_CALLING_CODE]: yup.mixed(),
    [FIELD.MOBILE_PHONE]: yup
      .string()
      .test(
        'validatePhone',
        'session.forget_passowrd.check_phone.error',
        (value, context) => {
          if (!value) {
            return true;
          }
          const parsePhone = parsePhoneNumber(
            `+${context.parent[FIELD.COUNTRY_CALLING_CODE]}${value}`,
          );
          const errors = validatePhone(
            parsePhone?.nationalNumber || '',
            parsePhone?.country,
            {
              validateTarget: {
                mobilePhoneValidation: true,
              },
            },
          );
          return errors.length === 0;
        },
      ),
    ...genderSchema,
    [FIELD.BIRTHDAY]: yup
      .object()
      .shape({
        year: yup.number().nullable(),
        month: yup.number().nullable(),
        date: yup.number().nullable(),
      })
      .test({
        name: 'custom',
        test: (value, ctx) => {
          const {
            birthday_format: birthdayFormat,
            minimum_age_limit: minAge,
            general_fields: generalFields,
          } = userSetting || {};
          const birthdaySetting =
            find(generalFields, {
              type: 'birthday',
            })?.options?.profile ?? {};

          const { year, month, date } = value;
          const errorCode = validateBirthday({
            birthdayFormat,
            year,
            month: getJsMonth(month),
            date,
            minAge,
            nullable: birthdaySetting.required !== BOOLEAN_STATUS.TRUE,
          });

          let message = null;
          switch (errorCode) {
            case BIRTHDAY_ERROR.INVALID:
              message = {
                translateKey: 'form.validation.birthday.invalid',
              };
              break;
            case BIRTHDAY_ERROR.MIN_AGE:
              message = {
                translateKey:
                  'form.validation.birthday.invalid.minimum_age_limit',
                params: { minimumAgeLimit: minAge },
              };
              break;
            case BIRTHDAY_ERROR.REQUIRED:
              message = {
                translateKey: 'form.validation.birthday.required',
              };
              break;
            case null:
              break;
            default:
              throw Error(`unknown birthday error: ${errorCode}`);
          }

          return message === null ? true : ctx.createError({ message });
        },
      }),
    [FIELD.PHONE]: yup.string().when([], {
      is: () => hasCountryCode,
      then: yup
        .string()
        .test('validatePhone', 'form.validation.invalid', (value, context) => {
          if (!value) {
            return true;
          }
          const parsePhone = parsePhoneNumber(
            `+${context.parent[FIELD.PHONE_COUNTRY_CODE]}${value}`,
          );
          const errors = validatePhone(
            parsePhone?.nationalNumber || '',
            parsePhone?.country,
          );
          return errors.length === 0;
        }),
      otherwise: PHONE_SCHEMA.notRequired()
        .nullable()
        .transform((value) => value ?? null),
    }),

    [FIELD.PHONE_COUNTRY_CODE]: yup.mixed(),
    [FIELD.DELIVERY_ADDRESSES]: yup.array().of(
      yup.lazy((value) =>
        yup.object().shape({
          [FIELD.RECIPIENT_NAME]: yup
            .string()
            .required('form.validation.required'),
          [FIELD.RECIPIENT_PHONE]: yup
            .string()
            .required('form.validation.required')
            .when([], {
              is: () => hasCountryCode,
              then: yup
                .string()
                .test(
                  'validatePhone',
                  'form.validation.invalid',
                  (value, context) => {
                    const parsePhone = parsePhoneNumber(
                      `+${
                        context.parent[FIELD.RECIPIENT_PHONE_COUNTRY_CODE]
                      }${value}`,
                    );
                    const errors = validatePhone(
                      parsePhone?.nationalNumber || '',
                      parsePhone?.country,
                    );
                    return errors.length === 0;
                  },
                ),
              otherwise: PHONE_SCHEMA,
            }),
          [FIELD.RECIPIENT_PHONE_COUNTRY_CODE]: yup.mixed(),
          ...(addressSchemaMap[value.country] || {}),
        }),
      ),
    ),
    [FIELD.CUSTOM_DATA]: yup.object().shape(customFields),
  });
};
